import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Search, Loader2, UserCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Buscador de matriculados (TS) que tienen cuenta de usuario.
 * Cuando se elige una sugerencia, llama a `onSelect(matriculado)` con un
 * objeto con todos los campos normalizados:
 *   { id, nombre, apellido, dni, telefono, email, matricula, distrito, titulo }
 */
export default function BuscadorMatriculados({ onSelect, placeholder, autoFocus }) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResultados([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_URL}/trabajadores/admin/buscar-con-usuario`,
          { params: { q: q.trim() }, headers: { Authorization: `Bearer ${token}` } }
        );
        setResultados(res.data || []);
        setOpen(true);
      } catch (err) {
        setResultados([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const handlePick = (m) => {
    onSelect && onSelect(m);
    setQ('');
    setResultados([]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid="buscador-matriculados">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder || 'Buscar por DNI, matrícula, nombre, apellido o email…'}
          className="pl-9"
          autoFocus={autoFocus}
          data-testid="buscador-matriculados-input"
        />
        {loading && (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
        )}
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid="buscador-matriculados-dropdown"
        >
          {resultados.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-gray-500">
              {q.trim().length < 2
                ? 'Escribí al menos 2 caracteres.'
                : 'No hay matriculados con usuario que coincidan.'}
            </div>
          ) : (
            resultados.map((m) => (
              <button
                type="button"
                key={m.id}
                onClick={() => handlePick(m)}
                className="w-full text-left px-3 py-2 hover:bg-violet-50 flex items-start gap-2 border-b last:border-b-0"
                data-testid={`buscador-result-${m.id}`}
              >
                <UserCheck className="w-4 h-4 mt-0.5 text-violet-600 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {m.nombre} {m.apellido}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    DNI {m.dni || '—'} · Mat. {m.matricula || '—'}
                    {m.email ? ` · ${m.email}` : ''}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
